import React, { Component } from "react";
import { shape, string, func, bool, arrayOf } from "prop-types";
import { connect } from "react-redux";
import { goBack, push } from "react-router-redux";

import { actions as financingActions, selectors as financingSelectors } from "reducers/financing";

import * as i18nUtils from "util/i18n";
import Notification from "pages/_components/Notification";
import Head from "pages/_components/Head";
import { resizableRoute } from "pages/_components/Resizable";
import { DownloadButton } from "v3/components/DownloadButton";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

import classNames from "classnames";
import MovementsFinancing from "./MovementsFinancing";

import FinancingSummaryDetail from "./FinancingSummaryDetail";

class FinancingDetail extends Component {
    static propTypes = {
        dispatch: func.isRequired,
        financingDetail: shape({}).isRequired,
        match: shape({
            params: shape({
                id: string,
            }),
        }).isRequired,
        financingFeed: arrayOf(shape({})).isRequired,
        listCategories: arrayOf(shape({})).isRequired,
        fetching: bool.isRequired,
        fetchingDownload: bool.isRequired,
        isDesktop: bool.isRequired,
        optionSelected: string.isRequired,
        lastDateFromFilter: string.isRequired,
        lastDateToFilter: string.isRequired,
        embeddedInV3Shell: bool,
    };

    static defaultProps = {
        embeddedInV3Shell: false,
    };

    componentDidMount() {
        const {
            dispatch,
            match: { params },
            listCategories,
            optionSelected,
        } = this.props;
        if (optionSelected === "toPay") {
            dispatch(financingActions.getFinancingDetailReques(params.id, listCategories, "3"));
        }
        this.syncEmbeddedV3ShellLayout();
    }

    componentDidUpdate(prevProps) {
        const { embeddedInV3Shell, isDesktop, financingDetail, match, fetchingDownload } = this.props;
        if (!embeddedInV3Shell) {
            return;
        }
        const shellOrViewportChanged =
            prevProps.embeddedInV3Shell !== embeddedInV3Shell || prevProps.isDesktop !== isDesktop;
        const detailChanged =
            prevProps.financingDetail?.numeroOperacion !== financingDetail?.numeroOperacion ||
            prevProps.match.params.id !== match.params.id;
        const mobileDepsChanged = !isDesktop && prevProps.fetchingDownload !== fetchingDownload;
        if (shellOrViewportChanged || detailChanged || mobileDepsChanged) {
            this.syncEmbeddedV3ShellLayout();
        }
    }

    componentWillUnmount() {
        const { embeddedInV3Shell, dispatch } = this.props;
        if (embeddedInV3Shell) {
            clearEmbeddedV3ShellLayoutState(dispatch);
        }
    }

    syncEmbeddedV3ShellLayout = () => {
        const { dispatch, embeddedInV3Shell, isDesktop, match, financingDetail, fetchingDownload } = this.props;
        const goBackPath =
            financingDetail?.descripcionCategoriaProducto != null
                ? `/financing/${encodeURIComponent(financingDetail.descripcionCategoriaProducto)}`
                : "/financing/categories";
        const headerTitle =
            financingDetail?.numeroOperacion != null
                ? `${financingDetail.descripcionCategoriaProducto || ""} ${i18nUtils.get("financing.detail.title")} ${
                      match.params.id
                  }`.trim()
                : match.params.id;
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["form"],
            headerTitle,
            goBackPath,
            rightContentMobile: !isDesktop ? (
                <DownloadButton
                    handleClick={this.handleExport}
                    isFetchingExport={fetchingDownload}
                    downloadImageWhite
                />
            ) : (
                undefined
            ),
        });
    };

    handleBack = () => {
        const { dispatch, embeddedInV3Shell, financingDetail } = this.props;
        dispatch(financingActions.setOptionSelected("toPay"));
        if (embeddedInV3Shell) {
            const path =
                financingDetail?.descripcionCategoriaProducto != null
                    ? `/financing/${encodeURIComponent(financingDetail.descripcionCategoriaProducto)}`
                    : "/financing/categories";
            dispatch(push(path));
            return;
        }
        dispatch(goBack());
    };

    getTitle = () => {
        const { match } = this.props;
        const { financingDetail } = this.props;

        const category = financingDetail?.descripcionCategoriaProducto;
        const name = `${category} ${i18nUtils.get("financing.detail.title")} ${match.params.id}`;
        const oneLoan = `${i18nUtils.get("financing.loan.title")} ${i18nUtils.get("financing.detail.title")} ${
            match.params.id
        }`;

        return (
            <h1 className="title-content white-space-wrap">
                {financingDetail?.descripcionCategoriaProducto === i18nUtils.get("financing.loans.title")
                    ? oneLoan
                    : name}
            </h1>
        );
    };

    renderPageHeader = (fetchingDownload, financingFeed, isDesktop) => {
        const { financingDetail, embeddedInV3Shell } = this.props;

        if (embeddedInV3Shell && !isDesktop) {
            return null;
        }

        return (
            <>
                {!embeddedInV3Shell && <Notification scopeToShow="form" />}
                <div className={classNames({ "admin-detail-head": isDesktop }, "px-0")}>
                    {isDesktop && <Head onBack={this.handleBack} />}
                    {isDesktop ? (
                        <Head
                            handleClick={this.handleExport}
                            exportList={financingFeed}
                            isFetchingExport={fetchingDownload}
                            handleClickMessage="global.download"
                            uniqueDownload
                            hasInlineButtons
                            centerElement={financingDetail && this.getTitle}
                            imageStyle="mr-2"
                        />
                    ) : (
                        <Head
                            headerClassName="blue-main-header-mobile text-nowrap"
                            handleClick={this.handleExport}
                            exportList={financingFeed}
                            isFetchingExport={fetchingDownload}
                            handleClickMessage="global.download"
                            uniqueDownload
                            centerElement={financingDetail && this.getTitle}
                            centerContentClassName="mx-5"
                            onBack={this.handleBack}
                            downloadImageWhite
                            addButtonImageWhite
                        />
                    )}
                </div>
            </>
        );
    };

    handleExport = (format) => {
        const {
            dispatch,
            financingFeed,
            financingDetail,
            lastDateFromFilter,
            lastDateToFilter,
            optionSelected,
        } = this.props;
        dispatch(
            financingActions.downloadListFeed(
                format,
                financingFeed,
                financingDetail,
                lastDateFromFilter,
                lastDateToFilter,
                optionSelected,
            ),
        );
    };

    render() {
        const {
            listCategories,
            match,
            financingFeed,
            financingDetail,
            fetchingDownload,
            isDesktop,
            fetching,
            optionSelected,
            lastDateFromFilter,
            lastDateToFilter,
        } = this.props;
        return (
            <div className="d-flex f-dir-col px-0">
                {this.renderPageHeader(fetchingDownload, financingFeed, isDesktop)}
                {financingDetail && <FinancingSummaryDetail financing={financingDetail} isDesktop={isDesktop} />}
                <MovementsFinancing
                    id={match.params.id}
                    listCategories={listCategories}
                    financingDetail={financingDetail}
                    financingFeed={financingFeed}
                    isDesktop={isDesktop}
                    optionSelected={optionSelected}
                    keyLabel="accounts.movements"
                    fetching={fetching}
                    filters={{
                        lastDateFrom: lastDateFromFilter,
                        lastDateTo: lastDateToFilter,
                    }}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    category: financingSelectors.getCategory(state),
    listCategories: financingSelectors.getFinancing(state),
    financingDetail: financingSelectors.getFinancingDetail(state),
    financingFeed: financingSelectors.getFinancingFeed(state),
    lastDateFromFilter: financingSelectors.getLastDateFromFilter(state),
    lastDateToFilter: financingSelectors.getLastDateToFilter(state),
    fetchingDownload: financingSelectors.getFetchingDownload(state),
    fetching: financingSelectors.getFetching(state),
    optionSelected: financingSelectors.getOptionSelected(state),
});

export default connect(mapStateToProps)(resizableRoute(FinancingDetail));
